/**
 * Float mirror of `LaunchToken.sol`'s bonding curve, floor ratchet, anti-snipe
 * fee and graduation check.
 *
 * WHY floats: the contract does everything in wei with rounding that always
 * favours the pool. The UI needs quotes, price-impact and chart maths, where a
 * sub-wei discrepancy is invisible but bigint gymnastics would cost readability
 * and speed. Rounding therefore differs from the chain by dust; the *mechanics*
 * (which bucket money lands in, and in what order) are reproduced exactly,
 * because those are what the interface is explaining to the user.
 *
 * Mechanics reproduced from the contract:
 *  - constant product on the *effective* reserves: `k = vQuote * vToken`,
 *    preserved by every trade (`LaunchToken._tokensOutForQuoteIn` et al).
 *  - a buy is split into fee / base-floor / anti-snipe / net-to-curve; only the
 *    net reaches the curve (`_splitBuy`).
 *  - the floor price is `max(prevFloor, baseFunded / redeemableSupply)` and is
 *    ratcheted **only by buys** (`_accrueAndRatchetFloor`). It is never lowered
 *    on any path — that is INV1 and the whole point of the product.
 *  - the anti-snipe fee accumulates in `snipeSurplus`, which is real backing but
 *    is invisible to the ratchet (the two-pool split, VIB-602), and is absorbed
 *    into the floor price as a one-time bump at graduation (`_graduate`).
 *  - a sell releases the seller's pro-rata floor backing so no extractable
 *    over-collateralisation is stranded (`_settleSell`, VIB-553).
 *  - a redeem burns tokens for `tokensIn * floorPrice` out of the floor reserve
 *    and leaves the floor price untouched (`redeemAtFloor`).
 */

import { weiToNumber } from '@/lib/format';
import type { DemoToken } from './types';

/** Basis-point denominator, as in the contract. */
export const BPS = 10_000;

/**
 * Blocks are the contract's clock for the anti-snipe decay (`block.number`), but
 * the demo world only has timestamps. Base-family chains target 2s blocks, so
 * elapsed blocks are modelled as elapsed-ms / 2000.
 */
export const BLOCK_MS = 2_000;

export interface CurveState {
  /** Effective ETH reserve `x`. Named "virtual" because the curve is priced on
   *  virtual reserves: it starts at `vQuote0` before a single ETH is raised. */
  vQuote: number;
  /** Effective token reserve `y` — also the remaining mintable inventory. */
  vToken: number;
  /** The reserve's starting value. `raised = vQuote - vQuote0`. */
  vQuote0: number;
  /** The token reserve's starting value — the curve's asymptotic supply cap. */
  vToken0: number;

  floorReserve: number;
  /** Anti-snipe portion of `floorReserve` not yet absorbed into `floorPrice`. */
  snipeSurplus: number;
  treasuryReserve: number;
  redeemableSupply: number;
  /** The stored, monotone-up floor price (ETH per token). */
  floorPrice: number;

  feeBPS: number;
  floorShareBPS: number;
  buybackFeeBPS: number;
  antiSnipeStartBPS: number;
  /** Decay length in blocks. 0 disables the anti-snipe fee entirely. */
  antiSnipeWindow: number;
  /** ms epoch of creation — the origin of the anti-snipe decay. */
  createdAt: number;
  graduationQuoteThreshold: number;

  graduated: boolean;
  tradingStopped: boolean;
}

/** The quote shape pages render, per DESIGN §8. */
export interface Quote {
  tokensOut: number;
  ethOut: number;
  avgPrice: number;
  feeEth: number;
  /** Fee share that ratchets the floor (base floor share + anti-snipe surplus). */
  floorAddEth: number;
  priceImpact: number;
  newSpotPrice: number;
  newFloorPrice: number;
}

export interface CurveResult {
  quote: Quote;
  /** The state the world would be in after the trade. Never mutates the input. */
  next: CurveState;
}

export const EMPTY_QUOTE: Quote = {
  tokensOut: 0,
  ethOut: 0,
  avgPrice: 0,
  feeEth: 0,
  floorAddEth: 0,
  priceImpact: 0,
  newSpotPrice: 0,
  newFloorPrice: 0,
};

const finite = (n: number): number => (Number.isFinite(n) ? n : 0);
const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

/* ────────────────────────────────────────────────────────────────────────────
   Views
   ──────────────────────────────────────────────────────────────────────────── */

/** The constant product the curve preserves. */
export function k(state: CurveState): number {
  return state.vQuote * state.vToken;
}

/** Marginal price, ETH per token — `quoteReserve / tokenReserve` in the contract. */
export function spotPrice(state: CurveState): number {
  if (state.vToken <= 0) return 0;
  return finite(state.vQuote / state.vToken);
}

/**
 * The **redeemable** floor: what `redeemAtFloor` would actually pay per token.
 *
 * This is the contract's STORED `floorPrice`, not a live ratio. That distinction
 * matters: the stored value only ever moves through {@link ratchet}, so it is
 * exactly monotone, whereas `floorReserve / redeemableSupply` wobbles by float
 * dust every time supply changes and would let the floor appear to tick down.
 * Use {@link floorPriceLive} when you want the over-collateralised ratio.
 *
 * The fallback covers a state that has never been ratcheted (a fresh curve).
 */
export function floorPrice(state: CurveState): number {
  if (state.floorPrice > 0) return state.floorPrice;
  if (state.redeemableSupply <= 0) return 0;
  const baseFunded = Math.max(0, state.floorReserve - state.snipeSurplus);
  return finite(baseFunded / state.redeemableSupply);
}

/** `floorReserve / redeemableSupply` over the whole reserve, incl. snipe surplus. */
export function floorPriceLive(state: CurveState): number {
  if (state.redeemableSupply <= 0) return 0;
  return finite(state.floorReserve / state.redeemableSupply);
}

/** Real ETH the curve has taken in — the figure graduation is measured against. */
export function raised(state: CurveState): number {
  return Math.max(0, state.vQuote - state.vQuote0);
}

export function graduationProgress(state: CurveState): number {
  if (state.graduationQuoteThreshold <= 0) return 0;
  return clamp(raised(state) / state.graduationQuoteThreshold, 0, 1);
}

/** Spot-priced value of the floor-backed supply. */
export function marketCap(state: CurveState): number {
  return spotPrice(state) * state.redeemableSupply;
}

export function floorRatio(state: CurveState): number {
  const spot = spotPrice(state);
  if (spot <= 0) return 0;
  return clamp(floorPrice(state) / spot, 0, 1);
}

/** Elapsed blocks since creation, the contract's `block.number - creationBlock`. */
export function elapsedBlocks(state: CurveState, now = Date.now()): number {
  return Math.max(0, Math.floor((now - state.createdAt) / BLOCK_MS));
}

/**
 * Extra buy-only fee, `antiSnipeStartBPS` at the creation block decaying linearly
 * to 0 at `creationBlock + antiSnipeWindow`. Mirrors `_antiSnipeBPS()`.
 */
export function antiSnipeBPS(state: CurveState, now = Date.now()): number {
  if (state.antiSnipeWindow <= 0 || state.antiSnipeStartBPS <= 0) return 0;
  const elapsed = elapsedBlocks(state, now);
  if (elapsed >= state.antiSnipeWindow) return 0;
  return (state.antiSnipeStartBPS * (state.antiSnipeWindow - elapsed)) / state.antiSnipeWindow;
}

/**
 * What a buyer actually pays right now: the standing `feeBPS` plus the decaying
 * anti-snipe surcharge. So it decays from `feeBPS + antiSnipeStartBPS` down to
 * `feeBPS` — the anti-snipe leg is additive in the contract, not a replacement.
 */
export function effectiveFeeBPS(state: CurveState, now = Date.now()): number {
  return state.feeBPS + antiSnipeBPS(state, now);
}

export function inAntiSnipeWindow(state: CurveState, now = Date.now()): boolean {
  return antiSnipeBPS(state, now) > 0;
}

/** Window end as a ms epoch, for countdowns. */
export function antiSnipeEndsAt(state: CurveState): number {
  return state.createdAt + state.antiSnipeWindow * BLOCK_MS;
}

/* ────────────────────────────────────────────────────────────────────────────
   Trades
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Applies the ratchet: the floor is set to the base pool's funding per token, but
 * only if that is higher. `Math.max` here is the float restatement of INV1 — the
 * one line in this file that must never be relaxed, since the entire UI (floor
 * rails, redeem CTA, safety meter) is a promise that this number cannot fall.
 */
function ratchet(state: CurveState): number {
  // The contract REVERTS (`FloorWouldDilute`) when a buy would push the base
  // ratio below the stored floor — reachable once buybacks have lifted the floor
  // above `floorShareBPS * price`. A demo must not freeze the trade panel, so here
  // the ratchet simply declines to move and `redeemAtFloor` stays capped by the
  // reserve: the world can never pay out ETH it does not hold, which is the part
  // of INV3 the interface is actually promising.
  if (state.redeemableSupply <= 0) return state.floorPrice;
  const baseFunded = Math.max(0, state.floorReserve - state.snipeSurplus);
  return Math.max(state.floorPrice, baseFunded / state.redeemableSupply);
}

export function buyExactIn(state: CurveState, ethIn: number, now = Date.now()): CurveResult {
  const spot = spotPrice(state);
  if (!(ethIn > 0) || state.tradingStopped || state.vToken <= 0) {
    return { quote: { ...EMPTY_QUOTE, newSpotPrice: spot, newFloorPrice: floorPrice(state) }, next: state };
  }

  const fee = (ethIn * state.feeBPS) / BPS;
  const baseFloorAmt = (ethIn * state.floorShareBPS) / BPS;
  const snipeAmt = (ethIn * antiSnipeBPS(state, now)) / BPS;
  const netIn = Math.max(0, ethIn - fee - baseFloorAmt - snipeAmt);

  const product = k(state);
  const newQuote = state.vQuote + netIn;
  const newToken = product / newQuote;
  const tokensOut = Math.max(0, state.vToken - newToken);

  const next: CurveState = {
    ...state,
    vQuote: newQuote,
    vToken: newToken,
    // The buyback carve-out is a redirection of the existing fee, not a surcharge.
    treasuryReserve: state.treasuryReserve + (ethIn * state.buybackFeeBPS) / BPS,
    floorReserve: state.floorReserve + baseFloorAmt + snipeAmt,
    snipeSurplus: state.snipeSurplus + snipeAmt,
    redeemableSupply: state.redeemableSupply + tokensOut,
  };
  next.floorPrice = ratchet(next);
  if (raised(next) >= next.graduationQuoteThreshold && next.graduationQuoteThreshold > 0) {
    graduateInPlace(next);
  }

  const newSpot = spotPrice(next);
  return {
    quote: {
      tokensOut,
      ethOut: 0,
      avgPrice: tokensOut > 0 ? ethIn / tokensOut : 0,
      feeEth: fee,
      floorAddEth: baseFloorAmt + snipeAmt,
      priceImpact: spot > 0 ? (newSpot - spot) / spot : 0,
      newSpotPrice: newSpot,
      newFloorPrice: floorPrice(next),
    },
    next,
  };
}

/**
 * Largest sell the curve will absorb before spot reaches the floor.
 *
 * `redeemAtFloor` is a standing bid at `floorPrice` that nobody can cancel, so the
 * curve cannot be pushed below it — a seller would redeem instead. Solving
 * `k / (vToken + size)^2 >= floorPrice` gives the closed form below, and the UI
 * uses it for the "max sellable" hint: past this point the exit is a redeem, not
 * a sell. (Enforcing it here is also what keeps `floorPrice <= spot` true on
 * every path, which every floor rail in the interface depends on.)
 */
export function maxSellBeforeFloor(state: CurveState): number {
  const fp = floorPrice(state);
  if (!(fp > 0)) return state.redeemableSupply;
  const limit = Math.sqrt(k(state) / fp) - state.vToken;
  return Math.max(0, Math.min(state.redeemableSupply, limit));
}

export function sellExactIn(state: CurveState, tokensIn: number): CurveResult {
  const spot = spotPrice(state);
  const size = Math.min(tokensIn, maxSellBeforeFloor(state));
  if (!(size > 0) || state.tradingStopped) {
    return { quote: { ...EMPTY_QUOTE, newSpotPrice: spot, newFloorPrice: floorPrice(state) }, next: state };
  }

  const product = k(state);
  const newToken = state.vToken + size;
  const newQuote = product / newToken;
  // A sell can never withdraw more than the curve has actually raised.
  const grossOut = Math.min(Math.max(0, state.vQuote - newQuote), raised(state));
  const fee = (grossOut * state.feeBPS) / BPS;
  const ethOut = Math.max(0, grossOut - fee);

  // Release this seller's pro-rata floor backing as their tokens burn, exactly as
  // a redeem would. Skipping it strands over-collateralisation that a later buy
  // could ratchet into and extract (VIB-553).
  const released = Math.min(state.floorReserve, floorPrice(state) * size);

  const next: CurveState = {
    ...state,
    vQuote: state.vQuote - grossOut,
    vToken: newToken,
    treasuryReserve: state.treasuryReserve + (grossOut * state.buybackFeeBPS) / BPS,
    floorReserve: Math.max(0, state.floorReserve - released),
    redeemableSupply: Math.max(0, state.redeemableSupply - size),
    // floorPrice is intentionally NOT ratcheted here: a burn transiently inflates
    // reserve/supply, and locking that peak in would let the next buy dilute the
    // floor below its backing (the buy-cheap / redeem-high drain).
  };

  const newSpot = spotPrice(next);
  return {
    quote: {
      tokensOut: 0,
      ethOut,
      avgPrice: size > 0 ? grossOut / size : 0,
      feeEth: fee,
      floorAddEth: 0,
      priceImpact: spot > 0 ? (newSpot - spot) / spot : 0,
      newSpotPrice: newSpot,
      newFloorPrice: floorPrice(next),
    },
    next,
  };
}

/**
 * The always-solvent exit. Pays the stored floor price and leaves it untouched,
 * so this path works after graduation and after a halt (INV5).
 */
export function redeemAtFloor(state: CurveState, tokensIn: number): CurveResult & { ethOut: number } {
  const fp = floorPrice(state);
  const size = Math.min(Math.max(0, tokensIn), state.redeemableSupply);
  const ethOut = Math.min(state.floorReserve, fp * size);

  const next: CurveState = {
    ...state,
    floorReserve: Math.max(0, state.floorReserve - ethOut),
    redeemableSupply: Math.max(0, state.redeemableSupply - size),
  };

  const spot = spotPrice(next);
  return {
    ethOut,
    quote: {
      tokensOut: 0,
      ethOut,
      avgPrice: fp,
      feeEth: 0,
      floorAddEth: 0,
      priceImpact: 0,
      newSpotPrice: spot,
      newFloorPrice: floorPrice(next),
    },
    next,
  };
}

/**
 * Fee-funded buyback: a `floorShareBPS` slice reinforces the floor, the remainder
 * buys on the curve and burns. The burned tokens never enter `redeemableSupply`,
 * so the burn leg cannot dilute the floor.
 */
export function executeBuyback(state: CurveState): CurveResult & { tokensBurned: number } {
  const spend = state.treasuryReserve;
  const spot = spotPrice(state);
  if (!(spend > 0) || state.tradingStopped) {
    return {
      tokensBurned: 0,
      quote: { ...EMPTY_QUOTE, newSpotPrice: spot, newFloorPrice: floorPrice(state) },
      next: state,
    };
  }

  const floorContribution = (spend * state.floorShareBPS) / BPS;
  const curveSpend = spend - floorContribution;
  const product = k(state);
  const newQuote = state.vQuote + curveSpend;
  const newToken = product / newQuote;
  const tokensBurned = Math.max(0, state.vToken - newToken);

  const next: CurveState = {
    ...state,
    vQuote: newQuote,
    vToken: newToken,
    treasuryReserve: 0,
    floorReserve: state.floorReserve + floorContribution,
  };
  next.floorPrice = ratchet(next);

  const newSpot = spotPrice(next);
  return {
    tokensBurned,
    quote: {
      tokensOut: tokensBurned,
      ethOut: 0,
      avgPrice: tokensBurned > 0 ? curveSpend / tokensBurned : 0,
      feeEth: 0,
      floorAddEth: floorContribution,
      priceImpact: spot > 0 ? (newSpot - spot) / spot : 0,
      newSpotPrice: newSpot,
      newFloorPrice: floorPrice(next),
    },
    next,
  };
}

/**
 * Closes the curve and absorbs the anti-snipe surplus into the redeemable floor
 * as a one-time bump. Safe only here, because trading is halted immediately after
 * — no later buy can be repriced up to the elevated floor and redeemed at a gain.
 */
export function graduateInPlace(state: CurveState): void {
  state.graduated = true;
  state.tradingStopped = true;
  if (state.snipeSurplus > 0 && state.redeemableSupply > 0) {
    const bumped = state.floorReserve / state.redeemableSupply;
    if (bumped > state.floorPrice) state.floorPrice = bumped;
  }
  state.snipeSurplus = 0;
}

/** Non-mutating variant, for previews. */
export function graduate(state: CurveState): CurveState {
  const next = { ...state };
  graduateInPlace(next);
  return next;
}

/** Price impact of a hypothetical buy without building a full quote. */
export function buyPriceImpact(state: CurveState, ethIn: number, now = Date.now()): number {
  return buyExactIn(state, ethIn, now).quote.priceImpact;
}

/* ────────────────────────────────────────────────────────────────────────────
   Bridge to the rendered data model
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Lifts a `DemoToken`'s raw (wei) curve fields into float `CurveState`.
 *
 * The token shape mirrors the contract's public getters, so this is the single
 * place that knows `quoteReserve`/`tokenReserve` are the *effective* reserves
 * while `virtualQuoteReserve`/`virtualTokenReserve` are their *initial* values.
 */
export function curveStateFromToken(token: DemoToken): CurveState {
  return {
    vQuote: weiToNumber(token.quoteReserve),
    vToken: weiToNumber(token.tokenReserve),
    vQuote0: weiToNumber(token.virtualQuoteReserve),
    vToken0: weiToNumber(token.virtualTokenReserve),
    floorReserve: weiToNumber(token.floorReserve),
    snipeSurplus: weiToNumber(token.snipeSurplus),
    treasuryReserve: weiToNumber(token.treasuryReserve),
    redeemableSupply: weiToNumber(token.redeemableSupply),
    floorPrice: weiToNumber(token.floorPrice),
    feeBPS: token.feeBPS,
    floorShareBPS: token.floorShareBPS,
    buybackFeeBPS: token.buybackFeeBPS,
    antiSnipeStartBPS: token.antiSnipeStartBPS,
    antiSnipeWindow: token.antiSnipeWindow,
    createdAt: token.createdAt,
    graduationQuoteThreshold: weiToNumber(token.graduationQuoteThreshold),
    graduated: token.graduated,
    tradingStopped: token.tradingStopped,
  };
}

/** The curve's price with nothing raised — where every chart's history begins. */
export function launchPrice(state: CurveState): number {
  if (state.vToken0 <= 0) return 0;
  return finite(state.vQuote0 / state.vToken0);
}
